import axios from 'axios';
import { Inventaire } from "@/models/Inventaire";
import { InventaireDepartement } from "@/models/InventaireDepartement";
import baseURL from '@/API';
import axiosInstance from "@/utils/axiosConfig";

const API_URL = baseURL + '/inventaire';

// Endpoints statiques pour les différentes actions
const ENDPOINTS = {
    INVENTAIRES: '/inventaire/all_inventaire_departement/',  // Endpoint DataTable pour fetchInventaires
    INVENTAIRES_EXPORT: '/inventaire/all_inventaire_departement/',
    INVENTAIRES_ARCHIVE: '/inventaire/archive_departement/'
};

interface ErrorResponse {
    status: number;
    message: string;
}

const InventoryDepartmentService = {

    /**
     * Crée un nouvel inventaire par département
     * @param inventaire - Données de l'inventaire à créer
     * @returns Status code ou erreur
     */
    async create(inventaire: InventaireDepartement): Promise<number | ErrorResponse> {
        try {
            const response = await axiosInstance.post(`${API_URL}/create_inventaire_departement/`, inventaire);
            return response.status;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                return {
                    status: error.response?.status ?? 500,
                    message: error.response?.data?.message ?? 'An error occurred while creating the department inventory.'
                };
            }
            return {
                status: 500,
                message: 'An unexpected error occurred.'
            };
        }
    },

    async getAll(url: string) {
        try {
            // Construire l'URL complète avec l'endpoint DataTable spécifique
            let fullUrl;
            if (url.startsWith('http')) {
                fullUrl = url;
            } else {
                // Utiliser l'endpoint DataTable spécifique pour les inventaires
                fullUrl = `${baseURL}${ENDPOINTS.INVENTAIRES}?${url}`;
            }

            console.log('🔍 INVENTORY DEPARTMENT SERVICE - URL complète:', fullUrl);

            // Détecter si c'est un export (Excel/CSV)
            const isExport = url.includes('export=excel') || url.includes('export=csv');
            
            // Si c'est un export, on attend un blob (fichier binaire)
            const response = await axiosInstance.get(fullUrl, {
                ...(isExport && { responseType: 'blob' })
            });
            
            // Si c'est un export, retourner directement la réponse blob
            if (isExport) {
                return response;
            }

            // Sinon, retourner les données formatées pour le DataTable
            return {
                data: response.data.data || response.data.results,
                totalRows: response.data.recordsFiltered || response.data.count,
                recordsFiltered: response.data.recordsFiltered || response.data.count,
                recordsTotal: response.data.recordsTotal || response.data.count,
                draw: response.data.draw
            };
        } catch (error: any) {
            console.error('Erreur dans InventoryDepartmentService.getAll:', error);
            return error;
        }
    },

    async extractAll(queryParams: string) {
        try {
            // Utiliser l'endpoint d'export spécifique
            const response = await axiosInstance.get(`${baseURL}${ENDPOINTS.INVENTAIRES_EXPORT}?${queryParams}`, {
                responseType: 'blob' // Indique qu'on attend un fichier binaire
            });
            return response;
        } catch (error: any) {
            console.error('Erreur dans InventoryDepartmentService.extractAll:', error);
            return error;
        }
    },

    async getInventaireArchive(url: string) {
        try {
            // Construire l'URL complète avec l'endpoint d'archive
            const fullUrl = url.startsWith('http') ? url : `${baseURL}${ENDPOINTS.INVENTAIRES_ARCHIVE}?${url}`;
            const response = await axiosInstance.get(fullUrl);
            return {
                data: response.data.data || response.data.results,
                totalRows: response.data.recordsFiltered || response.data.count,
                recordsFiltered: response.data.recordsFiltered || response.data.count,
                recordsTotal: response.data.recordsTotal || response.data.count,
                draw: response.data.draw
            };
        } catch (error) {
            if (axios.isAxiosError(error)) {
                return {
                    status: error.response?.status ?? 500,
                    message: error.response?.data?.message ?? 'An error occurred'
                };
            }
            return {
                status: 500,
                message: 'An unexpected error occurred'
            };
        }
    },
    async archiveInventaire({ id, commentaire, action }: { id: number, commentaire: string, action: string }) {
        try {
            const response = await axiosInstance.post(`${API_URL}/archive-inventaire-departement/`, {
                id,
                commentaire,
                action,
            });

            // Vérifiez le statut de la réponse et retournez les données
            if (response.status === 200) {
                return response.data; // Retourne { message: "Inventaire archived successfully" }
            } else {
                throw new Error(response.data?.message || 'Une erreur est survenue.');
            }
        } catch (error: any) {
            console.error('Erreur dans le service:', error.response?.data || error.message);
            throw new Error(error.response?.data?.message || error.message || 'Une erreur est survenue.');
        }
    },

    async AnnulerAffectation({ id }: { id: number }) {
        try {
            const response = await axiosInstance.post(`${API_URL}/annuler-affectation-departement/${id}/`)

            // Vérifiez le statut de la réponse et retournez les données
            if (response.status === 200) {
                return response.data; // Retourne { message: "Inventaire archived successfully" }
            } else {
                throw new Error(response.data?.message || 'Une erreur est survenue.');
            }
        } catch (error: any) {
            console.error('Erreur dans le service:', error.response?.data || error.message);
            throw new Error(error.response?.data?.message || error.message || 'Une erreur est survenue.');
        }
    },

    async getByIdInventaire(id: number): Promise<Inventaire | ErrorResponse> {
        try {
            const response = await axiosInstance.get(`${API_URL}/get_inventaire_departement/${id}/`);
            return response.data as Inventaire;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                return {
                    status: error.response?.status ?? 500,
                    message: error.response?.data?.message ?? 'An error occurred while retrieving the inventaire.'
                };
            }
            return {
                status: 500,
                message: 'An unexpected error occurred.'
            };
        }
    },

    async updateInventaire({ Id, Inventaire }: { Id: number, Inventaire: any }): Promise<number | ErrorResponse> {
        try {
            const response = await axiosInstance.put(`${API_URL}/update_inventaire_departement/${Id}/`, Inventaire);
            return response.status;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                return {
                    status: error.response?.status ?? 500,
                    message: error.response?.data?.message ?? 'An error occurred while updating the inventaire.'
                };
            }
            return {
                status: 500,
                message: 'An unexpected error occurred.'
            };
        }
    },

    async extractAllArchives(page: number, pageSize: number, sortBy: string, sortOrder: string, search: string) {
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                pageSize: pageSize.toString(),
                ordering: `${sortOrder}${sortBy}`,
                search: search,
                export: 'excel'
            });
            const response = await axiosInstance.get(`${API_URL}/archive_departement/?${params.toString()}`, {
                responseType: 'blob' // Indique qu'on attend un fichier binaire
            });
            return response;
        } catch (error: any) {
            return error;
        }
    },

};

export default InventoryDepartmentService;
